import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { setMetricsObject, track } from '../../app/metrics';
import type { Session } from '../../api/session';
import { getAllAddresses, getObjects, getPass, type ObjectInfo } from '../../api/tenant';
import { chooseObject, objectOfAddress } from '../../domain/objects';
import type { TelegramApi } from '../../telegram/webapp';
import { LoadFailed } from '../common/LoadFailed';
import { SplashScreen } from '../auth/screens';
import { NoObjectsScreen } from './NoObjectsScreen';
import { DeepLinkRedirect } from '../../app/DeepLinkRedirect';
import s from './objects.module.css';

const STORAGE_KEY = 'objectId';

interface Props {
  session: Session;
  telegram: TelegramApi;
  children: (object: ObjectInfo, objects: ObjectInfo[], switchTo: (id: number) => void) => ReactNode;
  /** Object from a посёлок link `startapp=obj_<id>` (roadmap P1-6): open it, or offer to join it. */
  linkedObjectId?: number;
  /** Pass from a notification `startapp=pass_<id>`: with no saved object, its object is opened instead of the picker. */
  linkedPassId?: number;
}

/** The resident's object that holds the pass, or null when it cannot be told (the picker is shown then). */
async function objectOfPass(session: Session, passId: number, objects: ObjectInfo[]): Promise<number | null> {
  try {
    const [pass, addresses] = await Promise.all([getPass(session, passId), getAllAddresses(session)]);
    const id = objectOfAddress(addresses, pass.address.id);
    return objects.some((o) => o.id === id) ? id : null;
  } catch {
    return null;
  }
}

/**
 * After login: no objects / choose / selected (spec §5.3). The choice lives in the platform storage (Telegram
 * CloudStorage, Max DeviceStorage); a pass link stands in for it when nothing is saved.
 */
export function ObjectGate({ session, telegram, children, linkedObjectId, linkedPassId }: Props) {
  const objects = useQuery({ queryKey: ['objects'], queryFn: () => getObjects(session) });
  const [selectedId, setSelectedId] = useState<number | null | undefined>(undefined);

  useEffect(() => {
    if (!objects.data || selectedId !== undefined) return;
    let cancelled = false;
    const list = objects.data;
    void telegram.storage.get(STORAGE_KEY).then(async (saved) => {
      if (cancelled) return;
      const linked = list.some((o) => o.id === linkedObjectId) ? String(linkedObjectId) : null;
      const choice = chooseObject(list, linked ?? saved);
      if (choice.kind === 'selected') return setSelectedId(choice.objectId);
      const fromPass = choice.kind === 'choose' && linkedPassId !== undefined ? await objectOfPass(session, linkedPassId, list) : null;
      if (cancelled) return;
      setSelectedId(fromPass);
      if (fromPass !== null) void telegram.storage.set(STORAGE_KEY, String(fromPass));
    });
    return () => {
      cancelled = true;
    };
  }, [objects.data, selectedId, telegram]);

  // Once per launch: how many objects the resident has (0 — needs a join request) — usage metrics.
  const counted = useRef(false);
  useEffect(() => {
    if (!objects.data || counted.current) return;
    counted.current = true;
    const n = objects.data.length;
    track('objects', { k: n > 1 ? '2+' : n });
  }, [objects.data]);
  useEffect(() => {
    setMetricsObject(selectedId ?? null);
    const name = objects.data?.find((x) => x.id === selectedId)?.name;
    // The посёлок name once per choice, so the metrics page shows names, not ids.
    if (name) track('object', { on: name.slice(0, 40) });
  }, [selectedId, objects.data]);

  const select = (id: number) => {
    setSelectedId(id);
    void telegram.storage.set(STORAGE_KEY, String(id));
  };

  if (objects.isError) return <LoadFailed error={objects.error} onRetry={() => objects.refetch()} fullScreen />;
  if (!objects.data || selectedId === undefined) return <SplashScreen />;

  if (objects.data.length === 0) {
    return <NoObjectsScreen session={session} telegram={telegram} onRecheck={() => void objects.refetch()} joinObjectId={linkedObjectId} />;
  }

  const current = objects.data.find((o) => o.id === selectedId);
  if (!current) {
    return (
      <main className={s.picker}>
        <h1 className={s.title}>Выберите посёлок</h1>
        <p className={s.lead}>Ваш номер есть в нескольких объектах. Переключиться можно в любой момент — на главной.</p>
        <div className={s.list}>
          {objects.data.map((o) => (
            <button key={o.id} type="button" className={s.item} onClick={() => select(o.id)}>
              <span className={s.itemName}>{o.name}</span>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                <path d="M9 6l6 6-6 6" />
              </svg>
            </button>
          ))}
        </div>
      </main>
    );
  }

  const foreign = linkedObjectId !== undefined && !objects.data.some((o) => o.id === linkedObjectId);
  return (
    <>
      {children(current, objects.data, select)}
      {foreign && <DeepLinkRedirect route={`/more/join?object=${linkedObjectId}`} />}
    </>
  );
}
